import { Box, Button, IconButton, Link, Typography, Stack } from '@mui/material'
import React, { useMemo } from 'react'
import InfoText from './InfoText'
import { useAccountLinkByAccountLinkId, useOwnerLocationOfInstallation } from '../hooks'
import { Installation } from 'src/Libs/installations/models'
import { ApiAccountType } from 'src/Libs/api'
import { getFullName } from 'src/Libs/utils/string-functions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { Link as RouterLink } from 'react-router-dom'
import AddressBlock from './AddressBlock'
import { format } from '@e3dc-react/shell/Libs/date'
import { K1Information, J2Editacontent } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { validate as isUuid } from 'uuid'
import { useHagerUserAccountId } from 'src/Libs/user/hooks/use-hager-user-account-id'
import { getCompactTableSx, tableCellButtonSx, tableCellIconButtonSx } from './table-styles'

// #region OwnerName
interface OwnerNameLinkProps {
  link: string
  ownerName: string
}

const OwnerNameLink: React.FC<OwnerNameLinkProps> = ({ link, ownerName }) => {
  return (
    <Link
      component={RouterLink}
      to={link}
      sx={{
        color: 'primary.main',
        overflowWrap: 'anywhere',
      }}
    >
      {ownerName}
    </Link>
  )
}
// #endregion

// #region StartAtComponent
interface StartAtComponentProps {
  owner: OwnerTableRow | undefined
}

const StartAtComponent: React.FC<StartAtComponentProps> = ({ owner }): React.ReactElement => {
  return (
    <Stack direction="row" alignItems="center">
      <Box component="span">{owner?.startAt}</Box>
      {owner?.editStartAt}
    </Stack>
  )
}

// #region EditStartAtButton
interface EditStartAtButtonProps {
  installation: Installation
  ownerAccountLinkData: AccountLinkModel
  refetchOwnerAccountLinkData: () => void
  refetchInstallationWithAttachedCompanies: () => void
}

const EditStartAtButton: React.FC<EditStartAtButtonProps> = ({
  installation,
  ownerAccountLinkData,
  refetchOwnerAccountLinkData,
  refetchInstallationWithAttachedCompanies,
}) => {
  const { openEditAccessStartAtModal } = useModals()
  const { t } = useCustomTranslation()

  return (
    <IconButton
      size="small"
      sx={tableCellIconButtonSx}
      aria-label={t('installation.details.info.edit-access-start-at.button-title')}
      onClick={() => {
        openEditAccessStartAtModal({
          startAt: new Date(ownerAccountLinkData.startAt),
          installationId: installation.id,
          accountLinkId: ownerAccountLinkData.id,
          refetchData: () => {
            refetchOwnerAccountLinkData()
            refetchInstallationWithAttachedCompanies()
          },
        })
      }}
    >
      <J2Editacontent />
    </IconButton>
  )
}
// #endregion

// #region ChangeOwnerButton
interface ChangeOwnerButtonProps {
  installation: Installation
  ownerAccountLinkData: AccountLinkModel | null
  refetchInstallationWithAttachedCompanies: () => void
}

const useStyles = makeStyles()(theme => ({
  changeCustomerButton: {
    '.MuiTypography-root': {
      fontSize: theme.typography.overline.fontSize,
    },
  },
}))

const ChangeOwnerButton: React.FC<ChangeOwnerButtonProps> = ({ installation, ownerAccountLinkData, refetchInstallationWithAttachedCompanies }) => {
  const { openChangeInstallationOwnerModal } = useModals()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <Button
      variant="text"
      className={classes.changeCustomerButton}
      onClick={() =>
        openChangeInstallationOwnerModal({
          installation,
          oldOwnerAccountLinkId: ownerAccountLinkData?.id ?? null,
          refetchData: refetchInstallationWithAttachedCompanies,
        })
      }
    >
      <Typography variant="overline">{t('installation.details.info.change-access-owner.button-title')}</Typography>
    </Button>
  )
}
// #endregion

// #region OwnerAccountLinkData
interface OwnerLink {
  link?: string
  name: string
}

interface OwnerAccountLinkDataProps {
  installation: Installation
  installationId: string | undefined
  isInstallationDecommissioned: boolean
  refetchInstallationWithAttachedCompanies: () => void
}

type OwnerTableRow = {
  ownerName: string
  accountId: string
  address: React.ReactNode
  email: string
  phone: string
  startAt: string
  editStartAt: React.ReactNode
  changeOwner: React.ReactNode
}

const OwnerAccountLinkData: React.FC<OwnerAccountLinkDataProps> = ({
  installation,
  installationId,
  isInstallationDecommissioned,
  refetchInstallationWithAttachedCompanies,
}) => {
  const { t } = useCustomTranslation()

  const ownerId = installation.ownerId
  const ownerType = installation.ownerAccountType
  const isHagerUser = ownerType === 'user' && !!installation.ownerAccountId && isUuid(installation.ownerAccountId)
  const { data: hagerUserAccountId, isLoading: hagerUserAccountIdLoading } = useHagerUserAccountId(installation.ownerUsername, isHagerUser)

  const [ownerAccountLinkData, , refetchOwnerAccountLinkData] = useAccountLinkByAccountLinkId(installationId, ownerId)

  const accountId = isHagerUser ? hagerUserAccountId ?? undefined : installation.ownerAccountId

  const ownerLinkData = useMemo(() => {
    let result: OwnerLink | null = null
    const userOwnerName = getFullName(installation?.ownerFirstName, installation?.ownerLastName)

    switch (ownerType) {
      case 'user':
        result = {
          link: accountId ? `/overview/users/${accountId}` : undefined,
          name: `${userOwnerName}`,
        }
        break

      case 'company':
        result = {
          link: `/overview/companies/${accountId}`,
          name: `${installation?.ownerCompanyName}`,
        }
        break

      case 'cloud':
        result = {
          name: userOwnerName,
        }
        break

      default:
        break
    }

    return result
  }, [installation, accountId, ownerType])

  const [ownerLocation, ownerLocationLoading] = useOwnerLocationOfInstallation(ownerType as ApiAccountType, accountId ? Number(accountId) : undefined)
  const address = useMemo(() => {
    const hasAddress = [ownerLocation?.street, ownerLocation?.zip, ownerLocation?.adress, ownerLocation?.country].some(elem => !!elem)

    if (!hasAddress) {
      return null
    }

    return {
      street: ownerLocation?.street,
      city: [`${ownerLocation?.zip} `, `${ownerLocation?.adress}`].join(''),
      country: ownerLocation?.country ? t(`common.countries.short.${ownerLocation.country}`) : undefined,
    }
  }, [ownerLocation]) //eslint-disable-line

  const email = ownerLocation?.email
  const phone = ownerLocation?.phone

  const getAccountIdRowValue = (accountLinkId: string | number | undefined, installationAccountId: string | undefined): string => {
    if (accountLinkId) {
      return `${accountLinkId}`
    }

    if (installationAccountId) {
      return installationAccountId
    }

    return '-'
  }

  if ((isHagerUser && hagerUserAccountIdLoading) || ownerLocationLoading) {
    return (
      <Box>
        <EditorSkeleton />
      </Box>
    )
  }

  const renderOwnerName = (owner: OwnerTableRow | undefined): string | React.ReactElement | undefined =>
    ownerLinkData?.link && owner?.ownerName !== '-' ? (
      <OwnerNameLink link={ownerLinkData.link} ownerName={owner?.ownerName || ''} />
    ) : (
      owner?.ownerName
    )

  const columns: Header<OwnerTableRow>[] = [
    {
      value: 'ownerName',
      label: t('installation.details.info.owner-information-name'),
      isSortable: false,
      valueRender: (_, __, owner) => renderOwnerName(owner),
    },
    {
      value: 'accountId',
      label: t('installation.details.info.owner-information-id'),
      isSortable: false,
    },
    {
      value: 'address',
      label: t('installation.details.info.owner-information-address'),
      isSortable: false,
    },
    {
      value: 'email',
      label: t('installation.details.info.owner-information-email'),
      isSortable: false,
    },
    {
      value: 'phone',
      label: t('installation.details.info.owner-information-phone'),
      isSortable: false,
    },
    {
      value: 'startAt',
      label: t('installation.details.info.owner-start-access-at'),
      isSortable: false,
      valueRender: (_, __, owner) => <StartAtComponent owner={owner} />,
    },
    {
      value: 'changeOwner',
      label: t('installation.details.info.change-access-owner.button-title'),
      isSortable: false,
      valueRender: value => value,
    },
  ]

  const tableData: OwnerTableRow[] = [
    {
      ownerName: ownerLinkData ? ownerLinkData.name : '-',
      accountId: getAccountIdRowValue(accountId, installation.ownerAccountId),
      address: address ? <AddressBlock street={address.street} city={address.city} country={address.country} /> : '-',
      email: email || '-',
      phone: phone || '-',
      changeOwner: isInstallationDecommissioned ? (
        '-'
      ) : (
        <Box sx={tableCellButtonSx}>
          <ChangeOwnerButton
            installation={installation}
            ownerAccountLinkData={ownerAccountLinkData}
            refetchInstallationWithAttachedCompanies={refetchInstallationWithAttachedCompanies}
          />
        </Box>
      ),
      startAt: ownerAccountLinkData?.startAt
        ? format(new Date(ownerAccountLinkData.startAt), t('installation.details.info.owner-start-access-at-format'))
        : '-',
      editStartAt:
        !isInstallationDecommissioned && ownerAccountLinkData ? (
          <EditStartAtButton
            installation={installation}
            ownerAccountLinkData={ownerAccountLinkData}
            refetchOwnerAccountLinkData={refetchOwnerAccountLinkData}
            refetchInstallationWithAttachedCompanies={refetchInstallationWithAttachedCompanies}
          />
        ) : null,
    },
  ]

  return (
    <Box sx={getCompactTableSx(-2)}>
      {!installation.ownerAccountId && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <InfoText value={t('installation.details.info.owner-not-set')} icon={<K1Information />} />
          {isInstallationDecommissioned ? null : (
            <ChangeOwnerButton
              installation={installation}
              ownerAccountLinkData={ownerAccountLinkData}
              refetchInstallationWithAttachedCompanies={refetchInstallationWithAttachedCompanies}
            />
          )}
        </Box>
      )}
      {installation.ownerAccountId && (
        <TableComplete
          header={columns}
          data={tableData}
          headerElevation={0}
          bodyElevation={0}
          footerElevation={0}
          disablePagination
          allowOtherCellColors
        />
      )}
    </Box>
  )
}

export default OwnerAccountLinkData
// #endregion
